import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link as ReactLink } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import {
  Alert,
  AlertDescription,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Stack,
} from '@chakra-ui/react';

import { BsCheckCircleFill, BsDashCircleFill } from 'react-icons/bs';

import { extractErrors, isInvalidField } from '@/lib/util';
import { renderErrors } from '@/ui/util';
import { PasswordHelper } from '@/ui/PasswordStrengthMeter';

import { updateMePasswordReset } from '@/store/mePasswordReset/actions';

import { useSchemaForUpdate } from './schema';

export function PasswordResetUpdate({ token }) {
  const { current } = useRef({});
  const dispatch = useDispatch();

  const [errors, setErrors] = useState([]);
  const [state, setState] = useState();
  const lockForm = state !== 'token_valid';
  const [showHelper, setShowHelper] = useState(false);

  const validationSchema = useSchemaForUpdate(current, !lockForm);

  useEffect(() => {
    // check if `reset_password_token` is still valid
    const user = { reset_password_token: token };
    dispatch(updateMePasswordReset({ user })) // check only, won't update because `password` is missing
      .unwrap()
      .then(({ errors, status }) => {
        if (status === 422) {
          // update failed (confirms token is valid)
          setState('token_valid');
        } else {
          // status === 401, token invalid or expired
          setState('token_invalid');
        }
      })
      .catch((e) => {
        console.error(e);
        setErrors(['System error']);
      });
  }, [token, setState, dispatch]);

  const onSubmit = useCallback(
    async (values, actions) => {
      try {
        const { password } = values;
        const reset_password_token = token;
        const user = { reset_password_token, password };
        const { errors, status } = await dispatch(
          updateMePasswordReset({ user })
        ).unwrap();
        // check errors
        if (status && status >= 400) {
          if (status === 401) {
            // token invalid or expired
            setState('token_invalid');
          } else {
            // validation errors
            extractErrors(errors, 'user', actions.setFieldError);
          }
        } else {
          setState('update_success');
        }
      } catch (e) {
        console.error(e);
        setErrors(['System error']);
      } finally {
        actions.setSubmitting(false);
      }
    },
    [token, setState, setErrors, dispatch]
  );

  return (
    <Stack
      spacing={8}
      mx="auto"
      w="lg"
      minH="lg"
      py={8}
      px={8}
      alignItems="center"
    >
      <Stack alignItems="center" textAlign="center">
        <Heading fontSize="4xl" color="secondary-900">
          Reset your password
        </Heading>
      </Stack>
      <Formik
        initialValues={{}}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {(formik) => (
          <Stack spacing={8}>
            {errors.map((error, idx) => (
              <Alert key={idx} status="error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ))}
            {formik.errors.base ? (
              <Alert status="error">
                <AlertDescription>
                  {renderErrors('Failed:', formik.errors.base)}
                </AlertDescription>
              </Alert>
            ) : null}
            {state === 'token_invalid' ? (
              <>
                <Alert status="error">
                  <AlertDescription>
                    Sorry, the password reset link is invalid or has expired.
                  </AlertDescription>
                </Alert>
                <Stack alignItems="center">
                  <Link
                    as={ReactLink}
                    to="/password_reset"
                    color="brand.500"
                    fontWeight="400"
                    textDecoration="underline"
                  >
                    Request another password reset link
                  </Link>
                </Stack>
              </>
            ) : null}
            {state === 'update_success' ? (
              <>
                <Alert status="success">
                  <AlertDescription data-test-id="alert-description">
                    Your password has been reset successfully!
                  </AlertDescription>
                </Alert>
                <Stack alignItems="center">
                  <Link
                    as={ReactLink}
                    to="/login"
                    color="brand.500"
                    fontWeight="400"
                    textDecoration="underline"
                    data-test-id="return-to-login-link"
                  >
                    Return to login
                  </Link>
                </Stack>
              </>
            ) : null}
            {state === 'token_valid' ? (
              <Form>
                <Stack spacing={4}>
                  <Field name="password">
                    {({ field, form, meta }) => (
                      <FormControl
                        isInvalid={isInvalidField(form, meta)}
                        position="relative"
                      >
                        <FormLabel
                          htmlFor={field.name}
                          fontSize="14px"
                          fontWeight="semiBold"
                        >
                          New password
                        </FormLabel>
                        <InputGroup>
                          <Input
                            {...field}
                            id={field.name}
                            value={field.value || ''}
                            type="password"
                            disabled={lockForm}
                            onFocus={() => setShowHelper(true)}
                            onBlur={() => setShowHelper(false)}
                          />
                          <InputRightElement>
                            {!meta.touched ? null : (
                              <Icon
                                as={
                                  meta.error
                                    ? BsDashCircleFill
                                    : BsCheckCircleFill
                                }
                                color={meta.error ? 'red.500' : 'green.500'}
                                boxSize="20px"
                              />
                            )}
                          </InputRightElement>
                        </InputGroup>
                        <FormErrorMessage
                          flexDir="column"
                          alignItems="flex-start"
                          w="calc(100% - 128px)"
                        >
                          {meta.error === 'password-complexity'
                            ? ''
                            : renderErrors('New Password', meta.error)}
                        </FormErrorMessage>
                        {showHelper && (
                          <PasswordHelper
                            right="0"
                            top="100%"
                            mt="8px"
                            value={formik?.values?.password}
                          />
                        )}
                      </FormControl>
                    )}
                  </Field>
                  <Field name="password_confirmation">
                    {({ field, form, meta }) => (
                      <FormControl isInvalid={isInvalidField(form, meta)}>
                        <FormLabel
                          htmlFor={field.name}
                          fontSize="14px"
                          fontWeight="semiBold"
                        >
                          Confirm password
                        </FormLabel>
                        <Input
                          {...field}
                          id={field.name}
                          value={field.value || ''}
                          type="password"
                          disabled={lockForm}
                        />
                        <FormErrorMessage
                          flexDir="column"
                          alignItems="flex-start"
                        >
                          {renderErrors('', meta.error)}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </Stack>
                <Stack py={6}>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={lockForm}
                    isLoading={formik.isSubmitting}
                    data-test-id="reset-password-button"
                  >
                    Reset password
                  </Button>
                </Stack>
              </Form>
            ) : null}
          </Stack>
        )}
      </Formik>
    </Stack>
  );
}
